import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  FaCaretRight as CaretRightIcon,
  FaCaretDown as CaretDownIcon,
} from 'react-icons/fa';

import DrillDetailColumnsLink from '../../reports/viewTaxExpensePage/drillDetailColumnsLink.component';

import {
  DATA,
  PARENT_DATA,
  PARENT_SUMMARY,
  PARENT_SUMMARY_DOUBLE,
  CHILD_DATA,
  CHILD_DATA_BOLD,

  DATA_BORDER_TOPLINE,
  DATA_BORDER_DOUBLEBOTTOMLINE,
  DATA_BORDER_TOPLINE_DOUBLE_BOTTOMLINE,
  TEXT,
} from './rowTypes';
import { ReportNumberRenderer } from './ReportNumberRenderer.component';
import { AsyncRowDataProvider } from './AsyncRowDataProvider.component';

const getColumnValue = column => column && column.value;
const columnHasValue = column => column && (
  Number.isInteger(column.value) || column.value || column.value === ''
);
const isColumnUnapproved = column => column && column.unapprovedFlag;

const wasAutoExpandExcluded = (rowId, rowsState) =>
  Object.values(rowsState).find(({ rowId: rowOverrideId, expanded }) =>
    rowId.startsWith(rowOverrideId) && !expanded
  );

const reportRowColumnCellPropType = PropTypes.shape({
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  unapprovedFlag: PropTypes.bool,
});

const reportRowColumnsPropType = PropTypes.oneOfType([
  PropTypes.arrayOf(reportRowColumnCellPropType),
  PropTypes.objectOf(reportRowColumnCellPropType),
]);

const reportRowDescriptionPropType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.oneOf([null]),
]);

class BodyRow extends Component {
  static propTypes = {
    columnNumbers: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
    row: PropTypes.shape({
      columns: reportRowColumnsPropType,
      children: PropTypes.arrayOf(PropTypes.shape({
        columns: reportRowColumnsPropType,
        description: reportRowDescriptionPropType,
      })),
      description: reportRowDescriptionPropType,
      expandable: PropTypes.bool,
      reportLineType: PropTypes.string,
      rowType: PropTypes.oneOf([
        DATA,
        CHILD_DATA,
        CHILD_DATA_BOLD,
        PARENT_DATA,
        PARENT_SUMMARY,
        PARENT_SUMMARY_DOUBLE,
        DATA_BORDER_TOPLINE,
        DATA_BORDER_DOUBLEBOTTOMLINE,
        DATA_BORDER_TOPLINE_DOUBLE_BOTTOMLINE,
        TEXT,
      ]),
      trailingText: PropTypes.string,
      preText: PropTypes.string,
      noEmptyRowOverride: PropTypes.bool,
      format: PropTypes.string,
      childrenDataUrl: PropTypes.string,
      summaryOnTop: PropTypes.bool,
      summaryDescription: PropTypes.string,
      summaryTrailingText: PropTypes.string,
      customClasses: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    reportWithPreText: PropTypes.bool.isRequired,
    isOddDataRow: PropTypes.bool,
    useCustomDecimalNumber: PropTypes.bool,
    suppressHidingDecimalPlacesForPercentage: PropTypes.bool,
    useBlankZeros: PropTypes.bool,
    useHistorical: PropTypes.bool,
    columnHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
    nestLevel: PropTypes.number,
    disableDrillLink: PropTypes.bool,
    rowId: PropTypes.string.isRequired,
    toggleRow: PropTypes.func.isRequired,
    shouldAutoExpandChildren: PropTypes.bool,
    rowsState: PropTypes.objectOf(PropTypes.shape({
      rowId: PropTypes.string,
      expanded: PropTypes.bool,
    })).isRequired,
  };

  static defaultProps = {
    isOddDataRow: false,
    useCustomDecimalNumber: false,
    nestLevel: 0,
  };

  componentDidMount() {
    const { shouldAutoExpandChildren, rowId, toggleRow, rowsState, row } = this.props;
    const { expandable } = row;
    const isExpanded = !!rowsState[rowId]?.expanded;
    const autoExpand = shouldAutoExpandChildren
      && expandable
      && !isExpanded
      && !wasAutoExpandExcluded(rowId, rowsState);

    if (autoExpand) {
      toggleRow(rowId, true);
    }
  }

  shouldComponentUpdate(nextProps) {
    const { rowsState } = this.props;
    const { rowsState: nextRowsState, ...restNextProps } = nextProps;

    if (Object.keys(restNextProps).some(propName => nextProps[propName] !== this.props[propName])) {
      return true;
    }

    const currentBranchRowIds = Object.keys(nextProps.rowsState).filter(rowId => {
      const parentHasChanged = this.props.rowId.startsWith(rowId);
      const childrenHasChanged = rowId.startsWith(this.props.rowId);
      return parentHasChanged || childrenHasChanged;
    });

    if (currentBranchRowIds.some(parentId => rowsState[parentId] !== nextProps.rowsState[parentId])) {
      return true;
    }

    return false;
  }

  componentDidUpdate(prevProps) {
    const { shouldAutoExpandChildren, rowId, row, toggleRow, rowsState } = this.props;
    const { expandable } = row;
    const isExpanded = !!rowsState[rowId]?.expanded;

    const autoExpandRow = shouldAutoExpandChildren && !prevProps.shouldAutoExpandChildren
      && expandable
      && !isExpanded
      && !wasAutoExpandExcluded(rowId, rowsState);

    if (autoExpandRow) {
      toggleRow(rowId, true);
    }
  }

  toggleRow = () => {
    const { toggleRow, rowId } = this.props;
    toggleRow(rowId);
  };

  renderNumber = (value, n) => {
    const {
      row,
      useCustomDecimalNumber,
      useBlankZeros,
      suppressHidingDecimalPlacesForPercentage,
      useHistorical,
      disableDrillLink,
    } = this.props;
    const { reportLineType, format } = row;

    if (reportLineType && !disableDrillLink) {
      return (
        <DrillDetailColumnsLink
          reportLineType={reportLineType}
          columnTitle={row.description}
          value={value}
          index={n}
          useHistorical={useHistorical}
          format={format}
          suppressHidingDecimalPlacesForPercentage={suppressHidingDecimalPlacesForPercentage}
          useCustomDecimalNumber={useCustomDecimalNumber}
          useBlankZeros={useBlankZeros}
        />
      );
    }

    if (value === null) {
      return null;
    }

    return (
      <ReportNumberRenderer
        key={n}
        value={value}
        format={format}
        suppressHidingDecimalPlacesForPercentage={suppressHidingDecimalPlacesForPercentage}
        useCustomDecimalNumber={useCustomDecimalNumber}
        useBlankZeros={useBlankZeros}
      />
    );
  };

  renderColumns = (columnNumbers, columns, rowType) => {
    const columnClassNames = classnames(
      'column', 'col-2', {
        'border-topline': rowType === DATA_BORDER_TOPLINE,
        'border-double-bottomline': rowType === DATA_BORDER_DOUBLEBOTTOMLINE,
        'border-topline-double-bottomline': rowType === DATA_BORDER_TOPLINE_DOUBLE_BOTTOMLINE,
      }
    );

    if (rowType === TEXT) {
      return columnNumbers.map(n => (
        <div className={columnClassNames} key={n}>
          <span>
            {getColumnValue(columns[n])}
          </span>
        </div>)
      );
    }

    const unapprovedBalanceIndicator = (
      <div className="with-indicator">
        {<div className="indicator-description">Balance includes unapproved tax entries.</div>}
      </div>
    );

    return columnNumbers.map(n => (
      <div className={columnClassNames} key={n}>
        <span className={classnames('value', { empty: !columnHasValue(columns[n]) })}>
          {this.renderNumber(getColumnValue(columns[n]), n)}
          {isColumnUnapproved(columns[n]) ? unapprovedBalanceIndicator : null}
        </span>
      </div>)
    );
  };

  // Workaround for position:sticky behaviour (description column hides when scrolling)
  renderEmptyColumns = () => this.props.columnNumbers.map((_, index) => (<div key={index} className="column col-2" />));

  renderChildrenRows = children => {
    const {
      columnHeaders,
      columnNumbers,
      reportWithPreText,
      useCustomDecimalNumber,
      suppressHidingDecimalPlacesForPercentage,
      useBlankZeros,
      nestLevel,
      rowId: parentRowId,
      toggleRow,
      shouldAutoExpandChildren,
      rowsState,
    } = this.props;

    return children.map((child, index) => (
      <BodyRow
        nestLevel={nestLevel + 1}
        key={index}
        rowId={`${parentRowId}.${index}`}
        toggleRow={toggleRow}
        shouldAutoExpandChildren={shouldAutoExpandChildren}
        rowsState={rowsState}
        columnHeaders={columnHeaders}
        columnNumbers={columnNumbers}
        row={child}
        reportWithPreText={reportWithPreText}
        useCustomDecimalNumber={useCustomDecimalNumber}
        suppressHidingDecimalPlacesForPercentage={suppressHidingDecimalPlacesForPercentage}
        useBlankZeros={useBlankZeros}
      />
    ));
  };


  render() {
    const {
      columnNumbers,
      row,
      reportWithPreText,
      isOddDataRow,
      columnHeaders,
      nestLevel,
      rowsState,
      rowId,
    } = this.props;
    const isOpen = !!rowsState[rowId]?.expanded;
    const {
      children,
      childrenDataUrl,
      columns,
      description,
      expandable,
      summaryOnTop,
      rowType,
      summaryDescription,
      summaryTrailingText,
      trailingText,
      preText,
      customClasses,
      noEmptyRowOverride,
    } = row;


    const bodyRowClassNames = classnames(
      'body-row',
      {
        bold: !(customClasses && customClasses.includes('no-bold'))
        && (nestLevel === 0 || rowType === CHILD_DATA_BOLD)
        && ![
          DATA,
          CHILD_DATA,
          DATA_BORDER_TOPLINE,
          DATA_BORDER_DOUBLEBOTTOMLINE,
          DATA_BORDER_TOPLINE_DOUBLE_BOTTOMLINE,
        ].includes(rowType),
        'data-row': rowType === DATA || rowType === CHILD_DATA || rowType === CHILD_DATA_BOLD,
        'parent-data-row': rowType === PARENT_DATA || rowType === TEXT,
        'parent-summary-row': rowType === PARENT_SUMMARY,
        'section-summary-row': rowType === PARENT_SUMMARY_DOUBLE,
        'border-double-bottomline-row': rowType === DATA_BORDER_DOUBLEBOTTOMLINE,
        'border-topline-row': rowType === DATA_BORDER_TOPLINE,
        'override-margin': noEmptyRowOverride,
      },
      customClasses
    );

    const trailingTextClassNames = classnames({
      'trailing-data-row': rowType === DATA || rowType === CHILD_DATA || rowType === CHILD_DATA_BOLD,
      'trailing-parent-data-row': rowType === PARENT_DATA,
      'trailing-parent-summary-row': rowType === PARENT_SUMMARY,
      'trailing-section-summary-row': rowType === PARENT_SUMMARY_DOUBLE,
    });

    const summaryTrailingTextClassNames = classnames({
      'trailing-parent-summary-row': rowType === DATA || rowType === PARENT_DATA || rowType === PARENT_SUMMARY,
      'trailing-section-summary-row': rowType === PARENT_SUMMARY_DOUBLE,
    });

    const toggle = isOpen
      ? <CaretDownIcon className="icon-toggle" onClick={this.toggleRow} size={16} />
      : <CaretRightIcon className="icon-toggle" onClick={this.toggleRow} size={16} />;

    const toggleClassNames = classnames('value', { expandable });

    const trailingComponent = trailingText
      ? <div className={trailingTextClassNames}>{trailingText}</div>
      : null;

    const summaryTrailingComponent = summaryTrailingText
      ? <div className={summaryTrailingTextClassNames}>{summaryTrailingText}</div>
      : null;

    const descriptionColumnClassNames = classnames(
      'column', 'col-4', 'description', {
        'short-description': reportWithPreText,
        'border-topline': rowType === DATA_BORDER_TOPLINE,
        'border-topline-double-bottomline': rowType === DATA_BORDER_TOPLINE_DOUBLE_BOTTOMLINE,
        'border-double-bottomline': rowType === DATA_BORDER_DOUBLEBOTTOMLINE,
      }
    );

    const pretextColumnClassNames = classnames(
      'col-1', 'column', 'pre-text', {
        'border-topline': rowType === DATA_BORDER_TOPLINE,
        'border-topline-double-bottomline': rowType === DATA_BORDER_TOPLINE_DOUBLE_BOTTOMLINE,
        'border-double-bottomline': rowType === DATA_BORDER_DOUBLEBOTTOMLINE,
      }
    );

    return (
      <div className={bodyRowClassNames}>
        <div className={classnames('row', { 'row-shaded': isOddDataRow, 'top-summary-row': summaryOnTop && isOpen })}>
          {reportWithPreText && (
            <div className={pretextColumnClassNames}>
              {preText || null}
            </div>
          )}

          <div className={descriptionColumnClassNames} style={{ paddingLeft: `${5 + nestLevel * 15}px` }}>
            <span className={toggleClassNames}>{expandable && toggle} {description}</span>
          </div>

          {!expandable && columns && this.renderColumns(columnNumbers, columns, rowType)}

          {expandable && (!isOpen || summaryOnTop) && columns && this.renderColumns(columnNumbers, columns, rowType)}

          {expandable && isOpen && !summaryOnTop && this.renderEmptyColumns(columnNumbers)}

          {!isOpen && trailingComponent}
        </div>

        {(children && isOpen) || (children && !expandable)
          ? (
            <div className={classnames('row', 'child-row')}>
              <div className="column col">
                { this.renderChildrenRows(children) }
              </div>
            </div>
          )
          : null
        }
        {
          (childrenDataUrl && isOpen)
            ? (
              <div className={classnames('row', 'child-row')}>
                <div className="column col">
                  <AsyncRowDataProvider
                    url={childrenDataUrl}
                    columnHeaders={columnHeaders}
                    columnNumbers={columnNumbers}
                    renderRows={this.renderChildrenRows}
                  />
                </div>
              </div>

            )
            : null
        }
        {expandable && isOpen && !summaryOnTop && (
          <div className={classnames('row', 'child-summary-row')}>
            <div className="column col-4 description">
              <span className="value">{summaryDescription}</span>
            </div>

            {columns && this.renderColumns(columnNumbers, columns)}

            {summaryTrailingComponent || trailingComponent}
          </div>
        )}
      </div>
    );
  }
}

export default BodyRow;
